import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, ServerError } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import type { GraphQLFormattedError } from 'graphql';

import generatedIntrospection from '~/graphql/generated/fragment';
import { clearJWT, getJWT } from '~/localstrage/jwt';
import { isIpad } from '~/utils/user_agent';

export const createApolloClient = () => {
  const httpLink = new HttpLink({
    uri: `${process.env.apiBaseUrl}/graphql`,
  });

  const authLink = new ApolloLink((operation, forward) => {
    const jwt = getJWT();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    operation.setContext(({ headers }: Record<string, any>) => {
      const contextHeader = {
        headers: {
          ...headers,
          credentials: 'include',
          Authorization: jwt ? `Bearer ${jwt}` : '',
          // TODO: Datadog をやめるときにヘッダー名を変える（api/config/initializers/opentelemetry.rb）
          'X-Datadog-Env': 'pharmacy-request',
        },
      };

      if (isIpad()) {
        // kinesissToBigQueryでdevice判定してる処理を将来的にフロントに移す予定
        // iPadOSだけはdevice判定できないため、先に移した
        contextHeader['headers']['X-Device-Type'] = 'tablet';
      }

      return contextHeader;
    });

    return forward(operation);
  });

  let hasRedirected = false;
  const errorLink = new ErrorLink(({ error }) => {
    let isAuthError = false;

    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach((gqlErr: GraphQLFormattedError) => {
        console.log(
          `[GraphQL error]: Message: ${gqlErr.message}, Location: ${gqlErr.locations}, Path: ${gqlErr.path}`,
        );
        // app/graphql/pharms_schema.rb
        isAuthError = isAuthError || gqlErr.extensions?.code === 'UNAUTHENTICATED';
      });
    }

    if (error instanceof ServerError) {
      console.log(`[Network error]: ${error}`);
      isAuthError = isAuthError || error.statusCode === 401;
    } else if (!CombinedGraphQLErrors.is(error)) {
      console.log(`[Network error]: ${error}`);
    }

    if (isAuthError && !hasRedirected) {
      hasRedirected = true;

      const hasExistJwt = !!getJWT();

      clearJWT();

      window.location.href = hasExistJwt
        ? `/login?next=${encodeURIComponent(window.location.href)}`
        : '/login';
    }
  });

  const cache = new InMemoryCache({
    possibleTypes: generatedIntrospection.possibleTypes,
    typePolicies: {
      WebBookingsSetting: {
        fields: {
          webBookingsExceptionBusinessHours: {
            merge: (_, incoming) => {
              return incoming;
            },
          },
        },
      },
      WebBookingsBusinessHour: {
        fields: {
          times: {
            merge: (_, incoming) => {
              return incoming;
            },
          },
        },
      },
      WebBookingsHolidayBusinessHour: {
        fields: {
          times: {
            merge: (_, incoming) => {
              return incoming;
            },
          },
        },
      },
      WebBookingsExceptionBusinessHour: {
        fields: {
          times: {
            merge: (_, incoming) => {
              return incoming;
            },
          },
        },
      },
      Mutation: {
        fields: {
          activateOtpSecret: {
            keyArgs: () => 'FILTERED',
          },
          upsertOrganization: {
            keyArgs: () => 'FILTERED',
          },
          sendPractitionerCodeMail: {
            keyArgs: () => 'FILTERED',
          },
          updatePractitionerAuthentication: {
            keyArgs: () => 'FILTERED',
          },
        },
      },
    },
  });

  const client = new ApolloClient({
    ssrMode: false,
    link: ApolloLink.from([errorLink, authLink, httpLink]),
    cache,
    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
    },
    devtools: {
      enabled: true,
    },
  });

  return client;
};
